import { useState, useEffect, useRef, useCallback } from 'react'
import { format } from 'date-fns'

const STAGES = [
  { id: 'New Lead', title: 'New Lead', color: 'gray', icon: '1' },
  { id: 'Site Visit', title: 'Site Visit', color: 'blue', icon: '2' },
  { id: 'Internal Approval', title: 'Internal Approval', color: 'orange', icon: '3' },
  { id: 'CEO Approval', title: 'CEO Approval', color: 'pink', icon: '4' },
  { id: 'Proposal Sent', title: 'Proposal Sent', color: 'yellow', icon: '5' },
  { id: 'Agreement Signed', title: 'Agreement', color: 'green', icon: '6' },
  { id: 'Facility Work', title: 'Facility', color: 'purple', icon: '7' },
  { id: 'Post Reki Approved', title: 'Post Reki', color: 'indigo', icon: '8' },
  { id: 'Gym Onboard', title: 'Gym Onboard', color: 'red', icon: '9' }
]

const getStageIndex = (stageId) => {
  return STAGES.findIndex(s => s.id === stageId)
}

const getStageColor = (stageId) => {
  const stage = STAGES.find(s => s.id === stageId)
  return stage?.color || 'gray'
}

const colorClasses = {
  gray: {
    bg: 'bg-gray-100',
    text: 'text-gray-700',
    border: 'border-gray-300',
    progress: 'bg-gray-500',
    light: 'bg-gray-50',
    badge: 'bg-gray-100 text-gray-800'
  },
  blue: {
    bg: 'bg-blue-100',
    text: 'text-blue-700',
    border: 'border-blue-300',
    progress: 'bg-blue-500',
    light: 'bg-blue-50',
    badge: 'bg-blue-100 text-blue-800'
  },
  yellow: {
    bg: 'bg-yellow-100',
    text: 'text-yellow-700',
    border: 'border-yellow-300',
    progress: 'bg-yellow-500',
    light: 'bg-yellow-50',
    badge: 'bg-yellow-100 text-yellow-800'
  },
  green: {
    bg: 'bg-green-100',
    text: 'text-green-700',
    border: 'border-green-300',
    progress: 'bg-green-500',
    light: 'bg-green-50',
    badge: 'bg-green-100 text-green-800'
  },
  purple: {
    bg: 'bg-purple-100',
    text: 'text-purple-700',
    border: 'border-purple-300',
    progress: 'bg-purple-500',
    light: 'bg-purple-50',
    badge: 'bg-purple-100 text-purple-800'
  },
  indigo: {
    bg: 'bg-indigo-100',
    text: 'text-indigo-700',
    border: 'border-indigo-300',
    progress: 'bg-indigo-500',
    light: 'bg-indigo-50',
    badge: 'bg-indigo-100 text-indigo-800'
  },
  orange: {
    bg: 'bg-orange-100',
    text: 'text-orange-700',
    border: 'border-orange-300',
    progress: 'bg-orange-500',
    light: 'bg-orange-50',
    badge: 'bg-orange-100 text-orange-800'
  },
  red: {
    bg: 'bg-red-100',
    text: 'text-red-700',
    border: 'border-red-300',
    progress: 'bg-red-500',
    light: 'bg-red-50',
    badge: 'bg-red-100 text-red-800'
  },
  pink: {
    bg: 'bg-pink-100',
    text: 'text-pink-700',
    border: 'border-pink-300',
    progress: 'bg-pink-500',
    light: 'bg-pink-50',
    badge: 'bg-pink-100 text-pink-800'
  }
}

const MobileKanbanView = ({
  gyms,
  onViewDetails,
  onLogVisit,
  onUploadDocuments,
  onPreRecce,
  onMoveStage,
  onRemoveGym,
  filters,
  setFilters,
  user,
  visibleStages
}) => {
  const [activeFilter, setActiveFilter] = useState('all')
  const [stageModal, setStageModal] = useState({ isOpen: false, gym: null })
  const [menuOpenId, setMenuOpenId] = useState(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const menuRef = useRef(null)
  const lastClickTime = useRef(0)

  // Debounce clicks to prevent double-tap issues on mobile
  const debounceClick = useCallback((callback) => {
    const now = Date.now()
    if (now - lastClickTime.current < 300) {
      return // Ignore if clicked within 300ms
    }
    lastClickTime.current = now
    callback()
  }, [])

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuOpenId && menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpenId(null)
      }
    }

    if (menuOpenId) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('touchstart', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
    }
  }, [menuOpenId])

  // Role-based restrictions
  const isBDExecutive = user?.role?.level === 4
  const isHeadOfAcquisition = user?.role?.level === 3
  const isCOO = user?.role?.level === 2
  const isCEO = user?.role?.level === 1

  // For BD Executive, only show New Lead, Site Visit, and Internal Approval stages
  const BD_STAGES = [
    { id: 'New Lead', title: 'New Lead', color: 'gray', icon: '1' },
    { id: 'Site Visit', title: 'Site Visit', color: 'blue', icon: '2' },
    { id: 'Internal Approval', title: 'Internal Approval', color: 'orange', icon: '3' }
  ]

  // Get stages to display based on role
  const displayStages = isBDExecutive ? BD_STAGES : visibleStages

  // Filter gyms based on role
  const roleFilteredGyms = isBDExecutive
    ? gyms.filter(gym => ['New Lead', 'Site Visit', 'Internal Approval'].includes(gym.stage))
    : gyms

  // Filter gyms based on active filter
  const filteredGyms = roleFilteredGyms.filter(gym => {
    if (activeFilter === 'all') return true
    return gym.stage === activeFilter
  })

  // Count gyms per stage (for BD Executive, only count relevant stages)
  const stageCounts = (isBDExecutive ? BD_STAGES : STAGES).reduce((acc, stage) => {
    acc[stage.id] = roleFilteredGyms.filter(g => g.stage === stage.id).length
    return acc
  }, {})

  const totalGyms = roleFilteredGyms.length

  // Get allowed stages for moving based on user role and current gym stage
  const getAllowedStages = (gym) => {
    if (!gym || !gym.stage) return []

    const currentIndex = getStageIndex(gym.stage)
    // If current stage is not recognized, don't allow any moves
    if (currentIndex === -1) return []

    const allowedStages = []

    STAGES.forEach((stage, index) => {
      // Can't move to same stage
      if (stage.id === gym.stage) return

      // Check role-based restrictions
      if (isBDExecutive) {
        // BD Executive can only move up to Internal Approval
        if (!['New Lead', 'Site Visit', 'Internal Approval'].includes(stage.id)) return
        // BD Executive can only move gyms assigned to them
        if (gym.assignedUserId !== user?.id) return
      }

      if (isHeadOfAcquisition) {
        // HOA can move up to Facility Work, and can trigger Gym Onboard for member data upload
        const hoaAllowed = ['Site Visit', 'Internal Approval', 'CEO Approval', 'Proposal Sent', 'Agreement Signed', 'Facility Work', 'Gym Onboard']
        if (!hoaAllowed.includes(stage.id)) return
      }

      if (isCOO) {
        // COO cannot move to Gym Onboard
        if (stage.id === 'Gym Onboard') return
      }

      // Can't skip more than 2 stages forward. Backward moves of any distance
      // are allowed (audited via stage history on the server).
      if (index - currentIndex > 2) return
      if (index === currentIndex) return

      const isBackward = index < currentIndex

      // Proof / CEO-approval requirements only apply to forward transitions.
      const requiresProof = !isBackward && (
        (gym.stage === 'CEO Approval' && stage.id === 'Proposal Sent') ||
        (gym.stage === 'Proposal Sent' && stage.id === 'Agreement Signed')
      )
      const requiresCEOApproval = !isBackward && gym.stage === 'CEO Approval' && stage.id === 'Proposal Sent'

      allowedStages.push({
        ...stage,
        isForward: !isBackward,
        isBackward,
        requiresProof,
        requiresCEOApproval,
        isDirectMove: (gym.stage === 'New Lead' && stage.id === 'Site Visit') ||
                     (gym.stage === 'Site Visit' && stage.id === 'Internal Approval')
      })
    })

    return allowedStages
  }

  // Check if user can move the gym
  const canUserMoveGym = (gym) => {
    if (!gym || !gym.stage) return false
    // If current stage is not recognized, don't allow any moves
    if (getStageIndex(gym.stage) === -1) return false

    if (isBDExecutive) {
      return gym.assignedUserId === user?.id
    }
    return true
  }

  // Handle stage selection with debounce
  const handleStageSelect = useCallback((gym, targetStage) => {
    if (isProcessing) return // Prevent double-clicks

    setIsProcessing(true)
    setStageModal({ isOpen: false, gym: null })

    if (onMoveStage) {
      onMoveStage(gym, targetStage)
    }

    // Reset processing state after a short delay
    setTimeout(() => {
      setIsProcessing(false)
    }, 500)
  }, [isProcessing, onMoveStage])

  return (
    <div className="space-y-4">
      {/* Header Stats */}
      <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-xl p-4 text-white">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-lg font-bold">Pipeline Overview</h2>
            <p className="text-red-100 text-sm">{totalGyms} total gyms</p>
          </div>
          <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-1">
            <span className="text-2xl font-bold">{totalGyms}</span>
          </div>
        </div>

        {/* Mini stage indicators - show only relevant stages for BD Executive */}
        <div className="flex items-center gap-1">
          {displayStages.map((stage, index) => {
            const count = stageCounts[stage.id] || 0
            const hasGyms = count > 0
            return (
              <div
                key={stage.id}
                className={`flex-1 h-2 rounded-full ${hasGyms ? 'bg-white' : 'bg-white/30'}`}
                title={`${stage.title}: ${count} gyms`}
              />
            )
          })}
        </div>

        {/* Stage counts for BD Executive - Clickable to filter */}
        {isBDExecutive && (
          <div className="flex items-center justify-around mt-3 pt-3 border-t border-white/20">
            {BD_STAGES.map(stage => {
              const count = stageCounts[stage.id] || 0
              const isActive = activeFilter === stage.id
              return (
                <button
                  key={stage.id}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    debounceClick(() => {
                      setActiveFilter(isActive ? 'all' : stage.id)
                    })
                  }}
                  className={`text-center px-4 py-2 rounded-lg transition-all touch-manipulation ${
                    isActive
                      ? 'bg-white/30 scale-105'
                      : 'hover:bg-white/10 active:bg-white/20'
                  }`}
                >
                  <div className="text-xl font-bold">{count}</div>
                  <div className="text-xs text-red-100">{stage.title}</div>
                </button>
              )
            })}
          </div>
        )}

        {/* Active filter indicator for BD Executive */}
        {isBDExecutive && activeFilter !== 'all' && (
          <div className="flex items-center justify-between mt-3 pt-2 border-t border-white/20">
            <span className="text-xs text-white/80">
              Showing: <span className="font-semibold">{activeFilter}</span>
            </span>
            <button
              onClick={() => setActiveFilter('all')}
              className="text-xs text-white/80 hover:text-white flex items-center gap-1"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Clear filter
            </button>
          </div>
        )}
      </div>

      {/* Stage Filter Pills - Hide for BD Executive */}
      {!isBDExecutive && (
        <div className="overflow-x-auto pb-2 -mx-4 px-4">
          <div className="flex gap-2 min-w-max">
            <button
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                debounceClick(() => setActiveFilter('all'))
              }}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap touch-manipulation ${
                activeFilter === 'all'
                  ? 'bg-red-600 text-white shadow-lg shadow-red-500/30'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 active:bg-gray-300'
              }`}
            >
              All ({totalGyms})
            </button>
            {visibleStages.map(stage => {
              const count = stageCounts[stage.id] || 0
              const colors = (colorClasses[getStageColor(stage.id)] || colorClasses.gray)
              const isActive = activeFilter === stage.id
              return (
                <button
                  key={stage.id}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    debounceClick(() => setActiveFilter(stage.id))
                  }}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap flex items-center gap-2 touch-manipulation ${
                    isActive
                      ? `${colors.progress} text-white shadow-lg`
                      : `${colors.bg} ${colors.text} hover:opacity-80 active:opacity-60`
                  }`}
                >
                  <span>{stage.title}</span>
                  <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                    isActive ? 'bg-white/30' : 'bg-white/70'
                  }`}>
                    {count}
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Gym Cards */}
      <div className="space-y-3">
        {filteredGyms.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h3 className="text-gray-900 font-medium">No gyms found</h3>
            <p className="text-gray-500 text-sm mt-1">
              {activeFilter !== 'all' ? 'Try selecting a different stage' : 'Add a new lead to get started'}
            </p>
          </div>
        ) : (
          filteredGyms.map(gym => {
            const stageIndex = getStageIndex(gym.stage)
            const progressPercent = ((stageIndex + 1) / STAGES.length) * 100
            const stageColor = getStageColor(gym.stage)
            const colors = colorClasses[stageColor]
            const canMove = canUserMoveGym(gym)
            const allowedStages = getAllowedStages(gym)

            return (
              <div
                key={gym.id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden active:scale-[0.98] transition-transform"
              >
                {/* Progress Bar */}
                <div className="h-1.5 bg-gray-100">
                  <div
                    className={`h-full ${colors.progress} transition-all duration-500`}
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>

                <div className="p-4">
                  {/* Header Row */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">{gym.name}</h3>
                      <div className="flex items-center gap-1 mt-1 text-sm text-gray-500">
                        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="truncate">{gym.location || 'No location'}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${colors.badge} whitespace-nowrap`}>
                        {gym.stage}
                      </span>
                      {/* Three-dot menu */}
                      <div className="relative" ref={menuOpenId === gym.id ? menuRef : null}>
                        <button
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            debounceClick(() => {
                              setMenuOpenId(menuOpenId === gym.id ? null : gym.id)
                            })
                          }}
                          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors touch-manipulation"
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM16 12a2 2 0 100-4 2 2 0 000 4z" />
                          </svg>
                        </button>
                        {menuOpenId === gym.id && (
                          <div className="absolute right-0 top-8 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                            <button
                              onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                setMenuOpenId(null)
                                onRemoveGym && onRemoveGym(gym)
                              }}
                              className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 rounded-lg flex items-center space-x-2 transition-colors touch-manipulation"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              <span>Remove Gym</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Stage Progress Dots - Use displayStages for BD Executive */}
                  <div className="flex items-center gap-1 mb-4">
                    {displayStages.map((stage, index) => {
                      // For BD Executive, calculate index within BD_STAGES
                      const bdStageIndex = isBDExecutive ? BD_STAGES.findIndex(s => s.id === gym.stage) : stageIndex
                      const isCompleted = index <= bdStageIndex
                      const isCurrent = index === bdStageIndex
                      const stageColors = colorClasses[stage.color] || colorClasses.gray
                      return (
                        <div key={stage.id} className="flex items-center flex-1">
                          <div
                            className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                              isCurrent
                                ? `${stageColors.progress} text-white ring-2 ring-offset-2 ring-${stage.color}-300`
                                : isCompleted
                                ? `${stageColors.progress} text-white`
                                : 'bg-gray-200 text-gray-400'
                            }`}
                            title={stage.title}
                          >
                            {isCompleted && !isCurrent ? (
                              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            ) : (
                              stage.icon
                            )}
                          </div>
                          {index < displayStages.length - 1 && (
                            <div className={`flex-1 h-0.5 mx-1 ${isCompleted ? stageColors.progress : 'bg-gray-200'}`} />
                          )}
                        </div>
                      )
                    })}
                  </div>

                  {/* Info Row */}
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                    <div className="flex items-center gap-3">
                      {gym.assignedUser && (
                        <div className="flex items-center gap-1">
                          <div className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center">
                            <svg className="w-3 h-3 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <span className="truncate max-w-[80px]">{gym.assignedUser.name}</span>
                        </div>
                      )}
                      {gym.updatedAt && (
                        <span>
                          Updated {format(new Date(gym.updatedAt), 'MMM d')}
                        </span>
                      )}
                    </div>
                    <span className="font-medium text-gray-700">
                      Step {(isBDExecutive ? BD_STAGES.findIndex(s => s.id === gym.stage) : stageIndex) + 1}/{displayStages.length}
                    </span>
                  </div>

                  {/* Pre-Recce Progress for Facility Work stage */}
                  {gym.stage === 'Facility Work' && (() => {
                    const forms = gym.preRecceForms || [];
                    const formTypes = [
                      { key: 'electrical', label: 'Electrical', icon: '⚡' },
                      { key: 'plumbing', label: 'Plumbing', icon: '🔧' },
                      { key: 'equipment_inventory', label: 'Equipment', icon: '🏋️' }
                    ];
                    const approvedCount = forms.filter(f => f.status === 'approved').length;
                    return (
                      <div className="mb-3 p-2.5 bg-purple-50 rounded-lg border border-purple-100">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-semibold text-purple-700">Pre-Recce Forms</span>
                          <span className={`text-xs font-bold ${
                            approvedCount === 3 ? 'text-green-600' : approvedCount > 0 ? 'text-amber-600' : 'text-gray-400'
                          }`}>{approvedCount}/3 Approved</span>
                        </div>
                        <div className="flex gap-2">
                          {formTypes.map(ft => {
                            const form = forms.find(f => f.formType === ft.key);
                            const status = form?.status || 'none';
                            const statusColors = {
                              none: 'bg-gray-100 text-gray-400 border-gray-200',
                              draft: 'bg-yellow-50 text-yellow-700 border-yellow-300',
                              submitted: 'bg-blue-50 text-blue-700 border-blue-300',
                              approved: 'bg-green-50 text-green-700 border-green-300',
                              rejected: 'bg-red-50 text-red-700 border-red-300'
                            };
                            return (
                              <div key={ft.key} className={`flex-1 text-center py-1.5 rounded-lg border text-xs font-medium ${statusColors[status]}`}>
                                <span>{ft.icon}</span>
                                {status === 'approved' ? <span className="ml-0.5">✓</span> :
                                 status !== 'none' ? <span className="ml-0.5 capitalize text-[10px]">{status}</span> : null}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <button
                      onClick={(e) => {
                        e.preventDefault()
                        debounceClick(() => onViewDetails(gym))
                      }}
                      className="flex-1 py-2.5 px-3 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 text-gray-700 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 touch-manipulation"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      View
                    </button>

                    {/* Move Stage Button - Show for users who can move */}
                    {canMove && allowedStages.length > 0 && (
                      <button
                        onClick={(e) => {
                          e.preventDefault()
                          debounceClick(() => setStageModal({ isOpen: true, gym }))
                        }}
                        disabled={isProcessing}
                        className="flex-1 py-2.5 px-3 bg-red-600 hover:bg-red-700 active:bg-red-800 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 touch-manipulation disabled:opacity-50"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                        Move
                      </button>
                    )}

                    {gym.stage === 'Site Visit' && (
                      <button
                        onClick={(e) => {
                          e.preventDefault()
                          debounceClick(() => onLogVisit(gym))
                        }}
                        className="flex-1 py-2.5 px-3 bg-green-600 hover:bg-green-700 active:bg-green-800 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 touch-manipulation"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Log Visit
                      </button>
                    )}

                    {gym.stage === 'Agreement Signed' && (
                      <button
                        onClick={(e) => {
                          e.preventDefault()
                          debounceClick(() => onUploadDocuments(gym))
                        }}
                        className="flex-1 py-2.5 px-3 bg-green-600 hover:bg-green-700 active:bg-green-800 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 touch-manipulation"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                        Upload
                      </button>
                    )}

                    {gym.stage === 'Facility Work' && (
                      <button
                        onClick={(e) => {
                          e.preventDefault()
                          debounceClick(() => onPreRecce && onPreRecce(gym))
                        }}
                        className="flex-1 py-2.5 px-3 bg-purple-600 hover:bg-purple-700 active:bg-purple-800 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 touch-manipulation"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                        </svg>
                        Pre-Recce
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* Bottom Summary - Hide for BD Executive since they have stage counts in header */}
      {filteredGyms.length > 0 && !isBDExecutive && (
        <div className="bg-gray-50 rounded-xl p-4 mt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Stage Distribution</h4>
          <div className="space-y-2">
            {displayStages.map(stage => {
              const count = stageCounts[stage.id] || 0
              const percent = totalGyms > 0 ? (count / totalGyms) * 100 : 0
              const colors = (colorClasses[getStageColor(stage.id)] || colorClasses.gray)
              return (
                <div key={stage.id} className="flex items-center gap-3">
                  <div className="w-20 text-xs text-gray-600 truncate">{stage.title}</div>
                  <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${colors.progress} transition-all duration-500`}
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                  <div className="w-8 text-xs font-medium text-gray-700 text-right">{count}</div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Stage Selection Modal */}
      {stageModal.isOpen && stageModal.gym && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md max-h-[80vh] overflow-hidden animate-slide-up">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-red-600 to-red-700 text-white px-5 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-lg">Move to Stage</h3>
                  <p className="text-red-100 text-sm mt-0.5">{stageModal.gym.name}</p>
                </div>
                <button
                  onClick={(e) => {
                    e.preventDefault()
                    if (!isProcessing) {
                      setStageModal({ isOpen: false, gym: null })
                    }
                  }}
                  disabled={isProcessing}
                  className="p-2 hover:bg-white/20 active:bg-white/30 rounded-full transition-colors touch-manipulation disabled:opacity-50"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Current Stage */}
            <div className="px-5 py-3 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Current Stage:</span>
                <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${(colorClasses[getStageColor(stageModal.gym.stage)] || colorClasses.gray).badge}`}>
                  {stageModal.gym.stage}
                </span>
              </div>
            </div>

            {/* Stage Options */}
            <div className="px-5 py-4 overflow-y-auto max-h-[50vh]">
              <p className="text-sm text-gray-500 mb-3">Select the target stage:</p>
              <div className="space-y-2">
                {getAllowedStages(stageModal.gym).map(stage => {
                  const stageColors = colorClasses[stage.color] || colorClasses.gray
                  return (
                    <button
                      key={stage.id}
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        handleStageSelect(stageModal.gym, stage.id)
                      }}
                      disabled={isProcessing}
                      className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all touch-manipulation disabled:opacity-50 disabled:cursor-not-allowed ${
                        stage.isBackward
                          ? 'border-amber-200 bg-amber-50 hover:border-amber-400 hover:bg-amber-100 active:bg-amber-200'
                          : 'border-green-200 bg-green-50 hover:border-green-400 hover:bg-green-100 active:bg-green-200'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${stageColors.progress}`}>
                          {stage.icon}
                        </div>
                        <div className="text-left">
                          <div className="font-medium text-gray-900">{stage.title}</div>
                          <div className="text-xs text-gray-500">
                            {stage.isBackward
                              ? 'Move back to a previous stage'
                              : stage.requiresCEOApproval
                                ? 'HoA / COO / CEO can approve'
                                : stage.isDirectMove
                                  ? stage.id === 'Site Visit'
                                    ? 'Move to start site visits'
                                    : 'Move for management review'
                                  : stage.requiresProof
                                    ? 'Move forward (proof required)'
                                    : 'Move forward'}
                          </div>
                        </div>
                      </div>
                      {isProcessing ? (
                        <div className={`w-5 h-5 border-2 border-t-transparent rounded-full animate-spin ${stage.isBackward ? 'border-amber-600' : 'border-green-600'}`} />
                      ) : (
                        <svg className={`w-5 h-5 ${stage.isBackward ? 'text-amber-600' : 'text-green-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          {stage.isBackward ? (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 17l-5-5m0 0l5-5m-5 5h12" />
                          ) : (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          )}
                        </svg>
                      )}
                    </button>
                  )
                })}

                {getAllowedStages(stageModal.gym).length === 0 && (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                    <p className="text-gray-500 text-sm">No available stages to move to</p>
                    <p className="text-gray-400 text-xs mt-1">Based on your role permissions</p>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-5 py-4 bg-gray-50 border-t border-gray-200">
              <button
                onClick={(e) => {
                  e.preventDefault()
                  setStageModal({ isOpen: false, gym: null })
                }}
                disabled={isProcessing}
                className="w-full py-3 px-4 bg-gray-200 hover:bg-gray-300 active:bg-gray-400 text-gray-700 rounded-xl font-medium transition-colors touch-manipulation disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  )
}

export default MobileKanbanView
